'use client';

import { useState } from 'react';
import { Loader2, Minus, Plus, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TopupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Price per briefing in cents */
  priceCents: number;
  /** Cancel URL path (e.g. /dashboard or /pricing) */
  cancelUrl?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TopupModal({
  open,
  onOpenChange,
  priceCents,
  cancelUrl = '/dashboard',
}: TopupModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const priceEur = priceCents / 100;
  const totalEur = priceEur * quantity;

  function handleQuantityChange(delta: number) {
    setQuantity((prev) => Math.min(5, Math.max(1, prev + delta)));
  }

  async function handleCheckout() {
    setIsLoading(true);

    try {
      const res = await fetch('/api/stripe/topup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity, cancelUrl }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(
          data.error || 'Checkout konnte nicht gestartet werden.',
        );
        return;
      }

      const { url } = await res.json();
      window.location.href = url;
    } catch {
      toast.error('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Briefing(s) kaufen</DialogTitle>
          <DialogDescription>
            Einzelne Briefings kaufen — Ihr Guthaben verfällt nicht.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Quantity selector */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10"
              onClick={() => handleQuantityChange(-1)}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>

            <div className="text-center min-w-[80px]">
              <span className="text-3xl font-bold text-foreground">
                {quantity}
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                {quantity === 1 ? 'Briefing' : 'Briefings'}
              </p>
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10"
              onClick={() => handleQuantityChange(1)}
              disabled={quantity >= 5}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Price breakdown */}
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Preis pro Briefing</span>
              <span className="text-foreground">
                &euro;{priceEur.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Menge</span>
              <span className="text-foreground">{quantity}</span>
            </div>
            <hr className="border-border" />
            <div className="flex items-center justify-between font-medium">
              <span className="text-foreground">Gesamtpreis</span>
              <span className="text-lg text-foreground">
                &euro;{totalEur.toLocaleString('de-DE', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Alle Preise zzgl. MwSt. Sichere Zahlung über Stripe.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          <Button onClick={handleCheckout} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Weiterleitung...
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Jetzt kaufen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
